/**
 * Service SEO pour optimiser le référencement
 */

export class SEOService {
  static generateMetaTitle(title, category = null, siteName = 'FlowDino') {
    if (!title) return siteName;
    
    const parts = [title];
    if (category) parts.push(category);
    parts.push(siteName);
    
    return parts.join(' | ');
  }

  static generateMetaDescription(content, maxLength = 160) {
    if (!content) return '';
    
    // Nettoyer le contenu HTML
    const cleanContent = content
      .replace(/<[^>]*>/g, '') // Supprimer les balises HTML
      .replace(/\s+/g, ' ') // Normaliser les espaces
      .trim();

    if (cleanContent.length <= maxLength) {
      return cleanContent;
    }

    // Tronquer au dernier mot complet
    const truncated = cleanContent.substring(0, maxLength);
    const lastSpace = truncated.lastIndexOf(' ');
    
    return lastSpace > 0 
      ? `${truncated.substring(0, lastSpace)}...`
      : `${truncated}...`;
  }

  static generateKeywords(content, additionalKeywords = []) {
    if (!content) return additionalKeywords.join(', ');

    // Extraire des mots-clés du contenu
    const words = content
      .toLowerCase()
      .replace(/<[^>]*>/g, '')
      .replace(/[^\w\sÀ-ÿ]/g, '')
      .split(/\s+/)
      .filter(word => word.length > 3)
      .filter(word => !this.stopWords.includes(word));

    // Compter la fréquence des mots
    const wordCount = {};
    words.forEach(word => {
      wordCount[word] = (wordCount[word] || 0) + 1;
    });

    // Trier par fréquence et prendre les plus fréquents
    const topWords = Object.entries(wordCount)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 8)
      .map(([word]) => word);

    return [...additionalKeywords, ...topWords].join(', ');
  }

  static generateStructuredData(type, data) {
    const baseSchema = {
      "@context": "https://schema.org",
      "@type": type
    };

    switch (type) {
      case 'Article':
        return {
          ...baseSchema,
          headline: data.title,
          description: data.description,
          author: {
            "@type": "Person",
            name: data.author || "FlowDino"
          },
          datePublished: data.publishedAt,
          dateModified: data.updatedAt || data.publishedAt,
          image: data.image,
          url: data.url,
          publisher: {
            "@type": "Organization",
            name: "FlowDino",
            logo: {
              "@type": "ImageObject",
              url: `${process.env.REACT_APP_BASE_URL}/react/images/logo.png`
            }
          }
        };

      case 'WebPage':
        return {
          ...baseSchema,
          name: data.title,
          description: data.description,
          url: data.url,
          isPartOf: {
            "@type": "WebSite",
            name: "FlowDino",
            url: process.env.REACT_APP_BASE_URL
          }
        };

      case 'Organization':
        return {
          ...baseSchema,
          name: "FlowDino",
          description: "Solution complète pour la gestion et le partage d'articles",
          url: process.env.REACT_APP_BASE_URL,
          logo: `${process.env.REACT_APP_BASE_URL}/react/images/logo.png`,
          contactPoint: {
            "@type": "ContactPoint",
            telephone: data.phone,
            contactType: "customer service",
            email: data.email
          },
          sameAs: data.socialLinks || []
        };

      case 'BreadcrumbList':
        return {
          ...baseSchema,
          itemListElement: data.breadcrumbs.map((item, index) => ({
            "@type": "ListItem",
            position: index + 1,
            name: item.name,
            item: item.url
          }))
        };

      default:
        return { ...baseSchema, ...data };
    }
  }

  static generateCanonicalUrl(path, baseUrl) {
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    return `${baseUrl}${cleanPath}`;
  }

  static generateAlternateLanguages(path, languages = ['fr', 'en']) {
    const baseUrl = process.env.REACT_APP_BASE_URL || 'https://www.flowdino.com';
    return languages.map(lang => ({
      lang,
      url: `${baseUrl}/${lang}${path}`
    }));
  }

  static optimizeImageAlt(filename, context = '') {
    // Générer un alt text basé sur le nom de fichier et le contexte
    const cleanName = filename
      .replace(/\.[^/.]+$/, '') // Supprimer l'extension
      .replace(/[-_]/g, ' ') // Remplacer les tirets et underscores
      .replace(/\b\w/g, l => l.toUpperCase()); // Capitaliser

    return context ? `${cleanName} - ${context}` : cleanName;
  }

  static validateSEO(data) {
    const errors = [];
    const warnings = [];

    // Validation du titre
    if (!data.title) {
      errors.push('Titre manquant');
    } else if (data.title.length > 60) {
      warnings.push('Titre trop long (>60 caractères)');
    } else if (data.title.length < 30) {
      warnings.push('Titre trop court (<30 caractères)');
    }

    // Validation de la description
    if (!data.description) {
      errors.push('Description manquante');
    } else if (data.description.length > 160) {
      warnings.push('Description trop longue (>160 caractères)');
    } else if (data.description.length < 120) {
      warnings.push('Description trop courte (<120 caractères)');
    }

    // Validation des mots-clés
    if (!data.keywords) {
      warnings.push('Mots-clés manquants');
    }

    // Validation de l'image
    if (!data.image) {
      warnings.push('Image Open Graph manquante');
    }

    return { errors, warnings, isValid: errors.length === 0 };
  }

  // Liste des mots vides en français
  static stopWords = [
    'le', 'de', 'et', 'à', 'un', 'il', 'être', 'et', 'en', 'avoir', 'que', 'pour',
    'dans', 'ce', 'son', 'une', 'sur', 'avec', 'ne', 'se', 'pas', 'tout', 'plus',
    'par', 'grand', 'end', 'le', 'bien', 'autre', 'alors', 'très', 'sans', 'nous',
    'mon', 'votre', 'mes', 'des', 'ces', 'nos', 'vos', 'leurs', 'elle', 'lui',
    'mais', 'car', 'donc', 'or', 'ni', 'aussi', 'puis', 'enfin', 'comme', 'si'
  ];
}

export default SEOService;
